import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router";
import { NavigationGuardProvider } from "@/contexts/navigation-guard-context";
import { FormBuilder } from "@/modules/form/components/form-builder";
import { FormNotFound } from "@/modules/form/components/form-not-found";
import Button from "@/components/ui/button";
import { useAuth } from "@/modules/auth";
import { useBrands } from "@/modules/brand";
import { useBrandKits } from "@/modules/brand/hooks/use-brand-kits";
import { useTags } from "@/modules/tag/hooks/use-tags";
import { TAG_COLORS } from "@/modules/tag/colors";
import { useForms, useFormQuestions, useFormTags } from "@/modules/form";
import { formPageAttributeApi } from "@/modules/form/services/form-page-attribute-api";
import { formPageApi } from "@/modules/form/services/form-page-api";
import { formPageFieldApi } from "@/modules/form/services/form-page-field-api";
import type { FlowSection, Question } from "@/modules/form/components/form-builder/types";
import type { TrackedChanges } from "@/modules/form/components/form-builder/hooks/use-form-builder-state";
import type { FormPage, FormQuestion, FormStatus } from "@/modules/form/types";
import Skeleton from "@/components/skeleton";

// Helper to map FormPage type to FlowSectionType
function mapPageTypeToFlowSectionType(pageType: string): FlowSection["type"] {
	const typeMap: Record<string, FlowSection["type"]> = {
		rating: "rating",
		welcome: "welcome",
		about_user: "personal-detail",
		about_company: "company-detail",
		consent: "consent",
		thank_you: "thank-you",
		private_feedback: "negative-feedback",
		negative_thank_you: "feedback-thank-you",
	};

	return typeMap[pageType] || "question";
}

// Helper to convert FormPage to FlowSection
function convertPageToFlowSection(page: FormPage): FlowSection {
	const titleAttr = page.attributes.find((attr) => attr.key === "title");
	const descriptionAttr = page.attributes.find((attr) => attr.key === "description");
	const imageUrlAttr = page.attributes.find((attr) => attr.key === "image_url");

	// Map fields if present (about_user, about_company, private_feedback pages)
	const fields = (page.fields ?? [])
		.sort((a, b) => a.sort_order - b.sort_order)
		.map((f) => ({
			id: f.id,
			field_key: f.field_key,
			field_type: f.field_type,
			label: f.label,
			placeholder: f.placeholder,
			is_required: f.is_required,
			is_visible: f.is_visible,
			sort_order: f.sort_order,
		}));

	return {
		id: page.id,
		type: mapPageTypeToFlowSectionType(page.type),
		title: titleAttr?.value || "",
		description: descriptionAttr?.value || "",
		image_url: imageUrlAttr?.value || null,
		enabled: page.status === "enabled",
		canToggle: page.type === "about_company" || page.type === "welcome",
		canReorder: false,
		canDelete: false,
		fields: fields.length > 0 ? fields : undefined,
	};
}

// Helper to convert FormQuestion to Question
function convertQuestionToQuestion(formQuestion: FormQuestion): Question {
	return {
		id: formQuestion.id,
		title: formQuestion.title || "",
		description: formQuestion.description || "",
		format: formQuestion.format,
		video_option_label: formQuestion.video_option_label || undefined,
		video_option_description: formQuestion.video_option_description || undefined,
		audio_option_label: formQuestion.audio_option_label || undefined,
		audio_option_description: formQuestion.audio_option_description || undefined,
		text_option_label: formQuestion.text_option_label || undefined,
		text_option_description: formQuestion.text_option_description || undefined,
	};
}

// Helper to convert local Question format to API format
function convertToApiFormat(format: Question["format"]): "text" | "video" | "audio" | "any" {
	return format;
}

export default function FormBuilderPage() {
	const { formId } = useParams<{ formId: string }>();
	const { currentWorkspaceId } = useAuth();
	const { brands } = useBrands();
	const { brandKit, viewBrandKit } = useBrandKits();
	const { tags, indexTags, searchTags, createTag, getTagsByIds, mergeSelectedTags } = useTags();
	const { showForm, updateForm } = useForms();
	const { indexQuestions, createQuestion, updateQuestion, deleteQuestion } = useFormQuestions();
	const { syncTags } = useFormTags();

	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [formTitle, setFormTitle] = useState("");
	const [initialSections, setInitialSections] = useState<FlowSection[]>([]);
	const [initialQuestions, setInitialQuestions] = useState<Question[]>([]);
	const [initialAutoApprove, setInitialAutoApprove] = useState(false);
	const [initialSelectedTagIds, setInitialSelectedTagIds] = useState<string[]>([]);
	const [initialTheme, setInitialTheme] = useState<"light" | "dark" | "system-default">("light");
	const [initialStatus, setInitialStatus] = useState<FormStatus>("unpublished");
	const [formSlug, setFormSlug] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [isFormNotFound, setIsFormNotFound] = useState(false);
	const [tagsNextCursor, setTagsNextCursor] = useState<string | null>(null);
	const [isLoadingMoreTags, setIsLoadingMoreTags] = useState(false);

	// Store form pages for section-to-attribute ID mapping
	const formPagesRef = useRef<FormPage[]>([]);

	// Store mapping from temporary question IDs to backend IDs
	const questionIdMapRef = useRef<Record<string, string>>({});

	// Get current brand info
	const currentBrand = brands?.find((b) => b.id === currentWorkspaceId);
	const brandName = currentBrand?.name ?? "Your Brand";

	// Fetch brand kit on mount
	useEffect(() => {
		if (currentWorkspaceId) {
			viewBrandKit(currentWorkspaceId);
		}
	}, [currentWorkspaceId, viewBrandKit]);

	// Fetch tags on mount
	useEffect(() => {
		if (currentWorkspaceId) {
			(async () => {
				const response = await indexTags(currentWorkspaceId);
				if (response) {
					setTagsNextCursor(response.meta.next_cursor);
				}
			})();
		}
	}, [currentWorkspaceId, indexTags]);

	const handleLoadMoreTags = useCallback(async () => {
		if (!currentWorkspaceId || !tagsNextCursor || isLoadingMoreTags) return;

		setIsLoadingMoreTags(true);
		const response = await indexTags(currentWorkspaceId, tagsNextCursor);
		if (response) {
			setTagsNextCursor(response.meta.next_cursor);
		}
		setIsLoadingMoreTags(false);
	}, [currentWorkspaceId, tagsNextCursor, isLoadingMoreTags, indexTags]);

	// Handle creating a new tag on demand
	const handleCreateTag = useCallback(
		(label: string, onCreated: (tagId: string) => void) => {
			if (!currentWorkspaceId) return;
			// Pick a random color from available colors
			const randomColor = TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)].value;
			// Create tag in background and call onCreated when done
			createTag(currentWorkspaceId, { label, color: randomColor }).then((response) => {
				if (response.data?.id) {
					onCreated(response.data.id);
				}
			});
		},
		[currentWorkspaceId, createTag],
	);

	// Handle searching tags
	const handleSearchTags = useCallback(
		async (query: string, cursor?: string | null) => {
			if (!currentWorkspaceId) return null;
			const response = await searchTags(currentWorkspaceId, query, cursor);
			if (response) {
				return {
					tags: response.data,
					hasMore: !!response.meta.next_cursor,
					nextCursor: response.meta.next_cursor,
				};
			}
			return null;
		},
		[currentWorkspaceId, searchTags],
	);

	// Handle tag selection - merge into store so it remains visible
	const handleTagSelect = useCallback(
		(tag: { id: string; label: string; color: string }) => {
			mergeSelectedTags([tag]);
		},
		[mergeSelectedTags],
	);

	// Load form data
	useEffect(() => {
		if (!currentWorkspaceId || !formId) return;

		(async () => {
			setIsLoading(true);
			setError(null);

			try {
				const [formResponse, questionsResponse] = await Promise.all([showForm(formId, currentWorkspaceId), indexQuestions(currentWorkspaceId, formId)]);

				if (!formResponse.success || !formResponse.data) {
					setIsFormNotFound(true);
					return;
				}

				const form = formResponse.data;
				setFormTitle(form.title);
				setFormSlug(form.slug);
				setInitialAutoApprove((form.auto_approve_submissions ?? 0) === 1);
				const selectedTagIds = form.tags?.map((tag) => tag.id) || [];
				setInitialSelectedTagIds(selectedTagIds);
				setInitialTheme(form.theme || "light");
				setInitialStatus(form.status || "unpublished");

				// Fetch and merge selected tags that may not be in the paginated list
				if (selectedTagIds.length > 0) {
					const selectedTags = await getTagsByIds(currentWorkspaceId, selectedTagIds);
					if (selectedTags) {
						mergeSelectedTags(selectedTags);
					}
				}

				// Convert pages to sections and store pages for attribute mapping
				if (form.pages && form.pages.length > 0) {
					formPagesRef.current = form.pages;
					const sections = form.pages.map(convertPageToFlowSection);
					setInitialSections(sections);
				}

				// Convert questions
				if (questionsResponse.success && questionsResponse.data) {
					const questions = questionsResponse.data.map(convertQuestionToQuestion);
					setInitialQuestions(questions);
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : "Failed to load form data");
			} finally {
				setIsLoading(false);
			}
		})();
	}, [currentWorkspaceId, formId, showForm, indexQuestions, getTagsByIds, mergeSelectedTags]);

	const handleSave = async (data: {
		formTitle: string;
		sections: FlowSection[];
		questions: Question[];
		autoApproveTestimonials: boolean;
		selectedTagIds: string[];
		theme: "light" | "dark" | "system-default";
		status: FormStatus;
		trackedChanges: TrackedChanges;
	}) => {
		if (!currentWorkspaceId || !formId) return;

		setIsSaving(true);
		setError(null);

		try {
			const promises: Promise<unknown>[] = [];
			const { trackedChanges } = data;

			// 1. Form settings changes (title, auto-approve, theme, status)
			if (
				trackedChanges.formSettings.formTitleChanged ||
				trackedChanges.formSettings.autoApproveChanged ||
				trackedChanges.formSettings.themeChanged ||
				trackedChanges.formSettings.statusChanged
			) {
				const formUpdate: { title?: string; auto_approve_submissions?: number; theme?: "light" | "dark" | "system-default"; status?: FormStatus } = {};
				if (trackedChanges.formSettings.formTitleChanged) {
					formUpdate.title = data.formTitle;
				}
				if (trackedChanges.formSettings.autoApproveChanged) {
					formUpdate.auto_approve_submissions = data.autoApproveTestimonials ? 1 : 0;
				}
				if (trackedChanges.formSettings.themeChanged) {
					formUpdate.theme = data.theme;
				}
				if (trackedChanges.formSettings.statusChanged) {
					formUpdate.status = data.status;
				}
				promises.push(updateForm(formId, currentWorkspaceId, formUpdate));
			}

			// 2. Tag sync (bulk)
			if (trackedChanges.tags.changed) {
				promises.push(syncTags(currentWorkspaceId, formId, trackedChanges.tags.currentIds));
			}

			// 3. Question changes
			// Delete removed questions
			for (const questionId of trackedChanges.questions.deleted) {
				// Use backend ID from mapping if exists, otherwise use the ID directly
				const backendId = questionIdMapRef.current[questionId] || questionId;
				promises.push(deleteQuestion(backendId, currentWorkspaceId, formId));
				// Clean up the mapping
				delete questionIdMapRef.current[questionId];
			}

			// Update existing questions
			for (const question of trackedChanges.questions.updated) {
				// Use backend ID from mapping if exists
				const backendId = questionIdMapRef.current[question.id] || question.id;
				promises.push(
					updateQuestion(backendId, currentWorkspaceId, formId, {
						title: question.title,
						description: question.description ?? "",
						format: convertToApiFormat(question.format),
						video_option_label: question.video_option_label,
						video_option_description: question.video_option_description,
						audio_option_label: question.audio_option_label,
						audio_option_description: question.audio_option_description,
						text_option_label: question.text_option_label,
						text_option_description: question.text_option_description,
					}),
				);
			}

			// Handle questions marked as "created" by change tracker
			for (const question of trackedChanges.questions.created) {
				const existingBackendId = questionIdMapRef.current[question.id];

				if (existingBackendId) {
					// Question was previously created, update it instead
					promises.push(
						updateQuestion(existingBackendId, currentWorkspaceId, formId, {
							title: question.title,
							description: question.description ?? "",
							format: convertToApiFormat(question.format),
							video_option_label: question.video_option_label,
							video_option_description: question.video_option_description,
							audio_option_label: question.audio_option_label,
							audio_option_description: question.audio_option_description,
							text_option_label: question.text_option_label,
							text_option_description: question.text_option_description,
						}),
					);
				} else {
					// Truly new question, create it
					promises.push(
						createQuestion(currentWorkspaceId, formId, {
							title: question.title,
							description: question.description ?? "",
							format: convertToApiFormat(question.format),
							video_option_label: question.video_option_label,
							video_option_description: question.video_option_description,
							audio_option_label: question.audio_option_label,
							audio_option_description: question.audio_option_description,
							text_option_label: question.text_option_label,
							text_option_description: question.text_option_description,
						}).then((response) => {
							if (response.success && response.data) {
								// Store mapping from temp ID to backend ID
								questionIdMapRef.current[question.id] = response.data.id;
							}
							return response;
						}),
					);
				}
			}

			// 4. Page attribute changes
			for (const attr of trackedChanges.pageAttributes) {
				const page = formPagesRef.current.find((p) => p.id === attr.sectionId);
				if (page) {
					const attribute = page.attributes.find((a) => a.key === attr.attributeKey);
					if (attribute) {
						promises.push(
							formPageAttributeApi.update(currentWorkspaceId, formId, page.id, attribute.id, {
								value: attr.newValue,
							}),
						);
					}
				}
			}

			// 5. Page-level updates (status changes)
			for (const pageUpdate of trackedChanges.pageUpdates) {
				promises.push(formPageApi.update(currentWorkspaceId, formId, pageUpdate.sectionId, { status: pageUpdate.status }));
			}

			// 6. Field updates (label/placeholder changes)
			for (const fieldUpdate of trackedChanges.fieldUpdates) {
				promises.push(formPageFieldApi.update(currentWorkspaceId, formId, fieldUpdate.sectionId, fieldUpdate.fieldId, fieldUpdate.changes));
			}

			await Promise.all(promises);

			// Update initial state after successful save
			// Keep temporary IDs for validation tracking - backend IDs are stored in questionIdMapRef
			setInitialSections(data.sections);
			setInitialQuestions(data.questions);
			setInitialAutoApprove(data.autoApproveTestimonials);
			setInitialSelectedTagIds(data.selectedTagIds);
			setInitialTheme(data.theme);
			setInitialStatus(data.status);

			// Update formPagesRef with new attribute values
			for (const attr of trackedChanges.pageAttributes) {
				const page = formPagesRef.current.find((p) => p.id === attr.sectionId);
				if (page) {
					const attribute = page.attributes.find((a) => a.key === attr.attributeKey);
					if (attribute) {
						attribute.value = attr.newValue;
					}
				}
			}

			// Update formPagesRef with new field values
			for (const fieldUpdate of trackedChanges.fieldUpdates) {
				const page = formPagesRef.current.find((p) => p.id === fieldUpdate.sectionId);
				if (page) {
					const field = page.fields?.find((f) => f.id === fieldUpdate.fieldId);
					if (field) {
						Object.assign(field, fieldUpdate.changes);
					}
				}
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to save form");
			throw err;
		} finally {
			setIsSaving(false);
		}
	};

	if (isLoading) {
		return (
			<div className="relative flex h-screen bg-[#fafafa]">
				<div className="w-100 shrink-0 bg-white border-r border-gray-200 flex flex-col h-screen">
					<div className="px-6 py-4 border-b border-gray-200">
						<Skeleton height={24} className="w-48" />
					</div>
					<div className="flex-1 overflow-y-auto p-6">
						<Skeleton height={200} className="w-full mb-4" />
						<Skeleton height={200} className="w-full mb-4" />
					</div>
				</div>
				<div className="flex-1 overflow-auto">
					<div className="p-12">
						<Skeleton height={400} className="w-full max-w-2xl mx-auto" />
					</div>
				</div>
			</div>
		);
	}

	if (isFormNotFound) {
		return <FormNotFound workspaceId={currentWorkspaceId || ""} />;
	}

	if (error) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="text-center">
					<h2 className="text-xl font-semibold text-primary mb-2">Error</h2>
					<p className="text-tertiary mb-4">{error}</p>
					<Button as={Link} to={`/${currentWorkspaceId}/forms`} variant="default">
						Back to Forms
					</Button>
				</div>
			</div>
		);
	}

	return (
		<NavigationGuardProvider>
			<FormBuilder
				formTitle={formTitle}
				formSlug={formSlug}
				brandName={brandName}
				brandLogo={brandKit?.logo_icon_with_label_url ?? null}
				brandKit={brandKit}
				tags={tags ?? []}
				initialSections={initialSections}
				initialQuestions={initialQuestions}
				initialAutoApprove={initialAutoApprove}
				initialSelectedTagIds={initialSelectedTagIds}
				initialTheme={initialTheme}
				initialStatus={initialStatus}
				onSave={handleSave}
				isLoading={isSaving}
				backUrl={`/${currentWorkspaceId}/forms`}
				onCreateTag={handleCreateTag}
				hasMoreTags={!!tagsNextCursor}
				isLoadingMoreTags={isLoadingMoreTags}
				onLoadMoreTags={handleLoadMoreTags}
				onSearchTags={handleSearchTags}
				onTagSelect={handleTagSelect}
			/>
		</NavigationGuardProvider>
	);
}
